import DeleteIcon from '@src/svgs/DeleteIcon';
import DownloadIcon from '@src/svgs/DownloadIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import RefreshIcon from '@src/svgs/RefreshIcon';
import applyImageOptimizations from '@src/utility/applyImageOptimizations';
import { handleDownloadMedia } from '@src/utility/general';
import { useClickAway } from '@src/utility/useClickAway';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import SpinningLoading from '../SpinningLoading';
import { useBRollUpload } from './BRollUploadContext';

import type { BRollAsset } from '@src/stores-v2/BRollStore';

type Props = {
  asset: BRollAsset;
};

const BRollUploadAsset = observer((props: Props) => {
  const { asset } = props;
  const { bRollService } = useBRollUpload();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [description, setDescription] = useState(asset.description);
  const [processingState, setProcessingState] = useState<
    'positive' | 'negative' | null
  >(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const actionMenuIconRef = useClickAway(() => setIsMenuOpen(false));

  const renderContent = () => {
    if (asset.type === 'photo') {
      return <AssetImage src={applyImageOptimizations(asset.url)} />;
    }
    return <AssetVideo src={asset.url} />;
  };

  const handleSaveDescription = async () => {
    if (description.trim() === asset.description) return;
    setProcessingState('positive');
    await bRollService.updateAssetDescription(asset, description.trim());
    setProcessingState(null);
  };

  const handleDeleteAsset = async () => {
    setIsMenuOpen(false);
    setProcessingState('negative');
    await bRollService.removeAsset(asset.id);
    setProcessingState(null);
  };

  const handleDownloadAsset = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (asset.status === 'pending') return;
    setIsMenuOpen(false);
    setProcessingState('positive');
    await handleDownloadMedia(e, {
      url: asset.url,
      id: asset.id,
    });
    setProcessingState(null);
  };

  const handleRetryUpload = async () => {
    setProcessingState('positive');
    await bRollService.retryFailedAsset(asset);
    setProcessingState(null);
  };

  const isUploading = asset.status === 'uploading';
  const isFailed = asset.status === 'failed';
  const isDisabled = Boolean(processingState) || isUploading;

  return (
    <Container>
      <Header>
        <AssetTag type={asset.type}>{asset.type}</AssetTag>

        {isFailed && (
          <>
            <RetryUploadButton
              onClick={handleRetryUpload}
              data-tooltip-id="retry-upload-tooltip"
              data-tooltip-content="Failed to upload. Click to retry."
            >
              <RefreshIcon strokeColor="var(--destructive)" />
            </RetryUploadButton>
            <Tooltip
              id="retry-upload-tooltip"
              style={{
                padding: '8px',
                fontSize: '12px',
                lineHeight: '120%',
                zIndex: '100005',
                fontWeight: 600,
              }}
              place="top"
              positionStrategy="fixed"
            />{' '}
          </>
        )}

        <ActionMenuIcon
          ref={actionMenuIconRef}
          isDisabled={isDisabled}
          onClick={isDisabled ? undefined : () => setIsMenuOpen(true)}
        >
          {processingState ? (
            <Spinner
              color={
                processingState === 'positive'
                  ? 'var(--primary)'
                  : 'var(--destructive)'
              }
            />
          ) : (
            <EllipsisIcon />
          )}
          {isMenuOpen && (
            <ActionMenu>
              {asset.status === 'uploaded' && (
                <ActionMenuItem onClick={handleDownloadAsset}>
                  Download
                  <DownloadIcon
                    width="14"
                    height="14"
                    strokeColor="var(--text-secondary)"
                  />
                </ActionMenuItem>
              )}
              <ActionMenuItem negative onClick={handleDeleteAsset}>
                Delete
                <DeleteIcon
                  width="14"
                  height="14"
                  strokeColor="var(--text-secondary)"
                />
              </ActionMenuItem>
            </ActionMenu>
          )}
        </ActionMenuIcon>
      </Header>
      <Content>
        <ContentMedia muted={asset.status === 'pending'}>
          {renderContent()}
        </ContentMedia>
        {isUploading && (
          <SpinningLoading
            Ico={null}
            text="Uploading..."
            contentStyle={{ gap: '12px' }}
            spinnerStyle={{ width: '24px', height: '24px' }}
            spinnerCircleStyle={{ borderWidth: '3px' }}
            textStyle={{ fontSize: '12px' }}
          />
        )}
      </Content>
      <DescriptionTextarea
        ref={inputRef}
        value={description}
        disabled={Boolean(processingState)}
        onChange={e => setDescription(e.target.value)}
        onBlur={handleSaveDescription}
        placeholder="Add description..."
      />
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 8px;
`;

const AssetTag = styled.div<{ type: 'photo' | 'video' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid
    ${props => (props.type === 'photo' ? 'var(--primary)' : '#006fee')};
  color: ${props => (props.type === 'photo' ? 'var(--primary)' : '#006fee')};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  text-transform: capitalize;
  margin-right: auto;
`;

const RetryUploadButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 24px;
  width: 24px;
  cursor: pointer;
  svg circle {
    transition: var(--transition-normal) ease;
  }
  &:hover svg circle {
    fill: var(--accent);
  }
`;

const ActionMenuIcon = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 24px;
  width: 24px;
  cursor: pointer;
  svg circle {
    transition: var(--transition-normal) ease;
  }
  &:hover svg circle {
    fill: var(--accent);
  }
  ${props =>
    props.isDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
`;

const ActionMenu = styled.div`
  position: absolute;
  top: 24px;
  right: 0;
  display: flex;
  flex-direction: column;
  background-color: var(--background);
  border-radius: 8px;
  border: 1px solid var(--border);
  box-shadow: var(--elevation-toast);
  overflow: hidden;
  z-index: 1;
`;

const ActionMenuItem = styled.div<{ negative?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  width: 112px;
  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
  &,
  svg {
    transition: var(--transition-normal) ease;
  }
  &:hover {
    color: var(--background);
    background-color: ${props =>
      props.negative ? 'var(--destructive)' : 'var(--primary)'};
    svg path {
      stroke: var(--background);
    }
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContentMedia = styled.div<{ muted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => (props.muted ? 0.4 : 1)};
`;

const AssetImage = styled.img`
  width: 100%;
  height: 144px;
  object-fit: cover;
  border-radius: 8px;
`;

const AssetVideo = styled.video`
  width: 100%;
  height: 144px;
  object-fit: cover;
  border-radius: 8px;
`;

const DescriptionTextarea = styled.textarea`
  height: 50px;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  resize: none;
  padding: 4px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: inherit;
  transition: var(--transition-normal) ease;
  &::placeholder {
    color: var(--text-muted);
  }
  &:focus {
    border-color: var(--border);
  }
  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const Spinner = styled.div<{ color?: string }>`
  border: 2px solid var(--border);
  border-radius: 50%;
  border-top: 2px solid ${props => props.color || 'var(--primary)'};
  border-left: 2px solid ${props => props.color || 'var(--primary)'};
  border-right: 2px solid ${props => props.color || 'var(--primary)'};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spinner 1s linear infinite;
  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export default BRollUploadAsset;
